import React from "react";
import FilterSidebar from "../../components/FilterSidebar";

/* ─── STATIC DATA ─── */

const AWARENESS_CARDS = [
    { label: "Awareness Sessions Conducted", value: "1,840",  sub: "Across all active centres",          color: "#0e7490" },
    { label: "Participants in Sessions",     value: "28,600", sub: "Total attendance across sessions",    color: "#2563eb" },
    { label: "Topics Covered",               value: "24",     sub: "Distinct awareness topics",           color: "#7c3aed" },
];

const TOPICS_DATA = [
    { name: "Health & Hygiene",    count: 5840 },
    { name: "Financial Literacy",  count: 4920 },
    { name: "Legal Rights",        count: 4280 },
    { name: "Nutrition",           count: 3960 },
    { name: "Digital Literacy",    count: 3640 },
    { name: "Govt Schemes",        count: 3180 },
    { name: "Environment",         count: 2780 },
];

const SOCIAL_CARDS = [
    { label: "Pre–Post Test Improvement",                    value: "+28%",  sub: "Avg knowledge gain post-session",          color: "#0e7490" },
    { label: "Beneficiaries Registered in Govt Schemes",     value: "6,840", sub: "Linked to at least one scheme",            color: "#2563eb" },
    { label: "% Improvement in Literacy Scores",             value: "34%",   sub: "Assessed pre/post literacy levels",        color: "#7c3aed" },
    { label: "Beneficiaries Starting Saving Habits",         value: "4,280", sub: "Piggy bank · bank account · SHG savings",  color: "#059669" },
    { label: "Women Reporting Increased Decision-Making",    value: "3,640", sub: "Household & community decisions",          color: "#d946ef" },
];

const PROGRAM_EXP = [
    { label: "Training Satisfaction Score", value: "4.3 / 5", sub: "Avg rating from exit surveys", color: "#0e7490" },
    { label: "Recommendation Score",         value: "81%",     sub: "Would recommend to peers",     color: "#059669" },
];

/* ─── HELPERS ─── */

function SectionBadge({ n, label }) {
    return (
        <div className="cd-section-hd">
            <span className="cd-section-badge">Section {n}</span>
            <span className="cd-section-title">{label}</span>
        </div>
    );
}

function StatCard({ label, value, sub, color }) {
    return (
        <div className="cd-card cd-stat" style={{ borderTop: `4px solid ${color}` }}>
            <div className="cd-stat-val" style={{ color }}>{value}</div>
            <div className="cd-stat-lbl">{label}</div>
            {sub && <div className="cd-stat-sub">{sub}</div>}
        </div>
    );
}

function VerticalBarChart({ data, nameKey = "name", valueKey = "count", height = 200, color1, color2 }) {
    const max = Math.max(...data.map(d => d[valueKey]));
    return (
        <div className="cd-vbar-wrap" style={{ height }}>
            {data.map(d => (
                <div key={d[nameKey]} className="cd-vbar-item">
                    <div className="cd-vbar-val">{d[valueKey].toLocaleString()}</div>
                    <div className="cd-vbar-track">
                        <div
                            className="cd-vbar-fill"
                            style={{
                                height: `${(d[valueKey] / max) * 100}%`,
                                background: `linear-gradient(to top, ${color1}, ${color2})`,
                            }}
                        />
                    </div>
                    <div className="cd-vbar-name">{d[nameKey]}</div>
                </div>
            ))}
        </div>
    );
}

/* ─── PAGE ─── */

export default function CommunityDevelopmentPage() {
    return (
        <div className="cd-wrapper">

            {/* ── MAIN ── */}
            <div className="cd-main">

                {/* Page Header */}
                <div className="cd-page-header">
                    <div>
                        <div className="cd-breadcrumb">Impact Dimensions</div>
                        <h1 className="cd-title">Community Development</h1>
                        <p className="cd-subtitle">Awareness outreach, social outcomes and programme experience across communities</p>
                    </div>
                    <div className="cd-badge">Static · FY 2024–25</div>
                </div>

                {/* ── SECTION 1: Awareness ── */}
                <SectionBadge n={1} label="Awareness" />
                <div className="cd-cards-3" style={{ marginBottom: 12 }}>
                    {AWARENESS_CARDS.map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* Topics bar chart */}
                <div className="cd-card cd-chart-card">
                    <div className="cd-chart-title">Number of Participants by Topic Covered</div>
                    <div className="cd-chart-note">Page filter: Topic</div>
                    <VerticalBarChart
                        data={TOPICS_DATA}
                        height={210}
                        color1="#0e7490"
                        color2="#67e8f9"
                    />
                </div>

                {/* ── SECTION 2: Social Outcomes ── */}
                <SectionBadge n={2} label="Social Outcomes" />

                {/* Row 1: 3 cards */}
                <div className="cd-cards-3" style={{ marginBottom: 12 }}>
                    {SOCIAL_CARDS.slice(0, 3).map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* Row 2: 2 cards */}
                <div className="cd-cards-2">
                    {SOCIAL_CARDS.slice(3).map(d => <StatCard key={d.label} {...d} />)}
                </div>

                {/* ── SECTION 3: Program Experience ── */}
                <SectionBadge n={3} label="Program Experience" />
                <div className="cd-cards-2">
                    {PROGRAM_EXP.map(d => <StatCard key={d.label} {...d} />)}
                </div>

            </div>

            {/* ── FILTER SIDEBAR ── */}
            <FilterSidebar />

            <style>{`
.cd-wrapper {
    display: flex;
    align-items: flex-start;
    background: #f8fafc;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    color: #111827;
}
.cd-main { flex: 1; min-width: 0; padding: 24px 28px; }

.cd-page-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 20px;
}
.cd-breadcrumb { font-size: 12px; color: #6b7280; font-weight: 700; margin-bottom: 4px; }
.cd-title      { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0 0 4px; }
.cd-subtitle   { font-size: 13px; color: #64748b; }
.cd-badge {
    background: #ecfeff; color: #0e7490;
    font-size: 11px; font-weight: 800; padding: 5px 12px;
    border-radius: 999px; white-space: nowrap;
}

.cd-section-hd {
    display: flex; align-items: center; gap: 10px; margin: 22px 0 10px;
}
.cd-section-badge {
    background: #0e7490; color: #fff;
    font-size: 10px; font-weight: 900; padding: 3px 9px;
    border-radius: 999px; white-space: nowrap; letter-spacing: 0.4px;
}
.cd-section-title { font-size: 14px; font-weight: 900; color: #0f172a; }

.cd-cards-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.cd-cards-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }

.cd-card {
    background: #fff; border: 1px solid #e5e7eb;
    border-radius: 12px; padding: 16px 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.cd-stat-val { font-size: 24px; font-weight: 900; line-height: 1; margin-bottom: 6px; }
.cd-stat-lbl { font-size: 12px; font-weight: 800; color: #374151; margin-bottom: 3px; }
.cd-stat-sub { font-size: 11px; color: #9ca3af; line-height: 1.3; }

.cd-chart-card  { padding: 18px 20px; }
.cd-chart-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 4px; }
.cd-chart-note  { font-size: 11px; color: #9ca3af; margin-bottom: 14px; }

.cd-vbar-wrap {
    display: flex; gap: 10px; align-items: flex-end; padding-bottom: 8px;
}
.cd-vbar-item {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; gap: 4px; height: 100%;
}
.cd-vbar-val  { font-size: 10px; font-weight: 800; color: #374151; }
.cd-vbar-track {
    flex: 1; width: 100%; max-width: 64px;
    background: #f1f5f9; border-radius: 6px 6px 0 0;
    display: flex; align-items: flex-end; overflow: hidden;
}
.cd-vbar-fill { width: 100%; border-radius: 6px 6px 0 0; transition: height 0.4s ease; }
.cd-vbar-name {
    font-size: 10px; font-weight: 700; color: #6b7280;
    text-align: center; line-height: 1.3; max-width: 76px;
}
            `}</style>
        </div>
    );
}
